import {
    isSuccess,
    failure,
    ResultKind,
} from '@superbet-group/web.lib.utilities';

import { logger } from '@app/logger';
import OfferEvent from '@core/models/offer/OfferEvent';
import { VideoProviderType } from '@core/models/offer/enums';
import { BetGeniusRequest } from '@models/streams/BetGenius';

import MediaServiceAPI from './MediaServiceAPI';
import MediaServiceCache from './MediaServiceCache';
import type { AtLeastOneVideoProvider } from './types';
import Vue from 'vue';
import {
    AnalyticsEventTypes,
    AnalyticsProtoEvents,
    MultimediaContentType,
    MultimediaRequestLocation,
    ParamTypes,
    Prefix,
    getParamWithPrefix,
} from '@src/app/utils/analyticsScreenNames';
import { Analytics as AnalyticsSDK } from '@superbet-group/web.analytics.sdk';
import {
    MultimediaContentType as MultimediaContentTypeEnum,
    MultimediaPosition,
    MultimediaRequestLocation as MultimediaRequestLocationEnum,
} from '@superbet-group/product.analytics';
export default class MediaService {
    constructor(
        private mediaServiceApi = new MediaServiceAPI(),
        private mediaServiceCache = new MediaServiceCache()) {}

    public async getMatchVideoStream(match: OfferEvent, userId: number) {
        try {
            if (!hasMatchVideoStream(match.streamInfo)) {
                throw new Error(`Unsupported stream, "${JSON.stringify(match.streamInfo ?? {})}"`);
            }

            if (this.mediaServiceCache.hasVideoStreamForMatch(match.id)) {
                return this.mediaServiceCache.getVideoStreamForMatch(match.id);
            }

            const response = await this.mediaServiceApi.getMatchVideoStream(match.streamInfo, userId);

            const isSuccessful = response.kind === ResultKind.Success ? 'true' : 'false';
            sendMultimediaRequestResultAnalytic(match, isSuccessful);

            if (isSuccess(response)) {
                this.mediaServiceCache.storeVideoStreamForMatch(match.id, response.value);
                return response.value;
            }

            throw response;
        } catch (e) {
            sendMultimediaRequestResultAnalytic(match, 'false', e as Error);
            handleMediaError(e as Error);
        }
    }

    public async getPerform(streamId: string, username: string, match: OfferEvent) {
        try {
            const response = await this.mediaServiceApi.getPerform(streamId, username);

            const isSuccessful = response.kind === ResultKind.Success ? 'true' : 'false';

            // We get a lot of false positives for Perform on web since getting the token and mounting the player will be successful,
            // but the player can still show an error message that isnt emitted
            sendMultimediaRequestResultAnalytic(match, isSuccessful);

            if (isSuccess(response)) {
                return response.value;
            }

            throw response;
        } catch (e) {
            sendMultimediaRequestResultAnalytic(match, 'false', e as Error);
            handleMediaError(e as Error);
        }
    }

    public async getBetGenius(request: BetGeniusRequest, match: OfferEvent) {
        try {
            const response = await this.mediaServiceApi.getBetGenius(request);

            const isSuccessful = response.kind === ResultKind.Success ? 'true' : 'false';
            sendMultimediaRequestResultAnalytic(match, isSuccessful);

            if (isSuccess(response)) {
                return response.value;
            }

            throw response;
        } catch (e) {
            sendMultimediaRequestResultAnalytic(match, 'false', e as Error);
            handleMediaError(e as Error);
        }
    }
}

function handleMediaError(e: Error) {
    logger.error(e);

    return failure({
        error: true,
        notice: '',
    });
}

function hasMatchVideoStream(x: Record<number, unknown>): x is AtLeastOneVideoProvider {
    return (
        x[VideoProviderType.IMG] !== undefined
        || x[VideoProviderType.INFRONTSPORTS] !== undefined
        || x[VideoProviderType.BETRADAR] !== undefined
        || x[VideoProviderType.BETER] !== undefined
    );
}

export function sendMultimediaRequestResultAnalytic(
    match: OfferEvent,
    isSuccessful: 'true' | 'false',
    error?: Error,
    additionalMessages: string[] = [],
) {
    const storagePosition = sessionStorage.getItem('StreamsAndVisualizationsContainerLastTarget') as keyof typeof MultimediaPositionType || 'top-right';
    const position = MultimediaPositionType[storagePosition];
    const paPosition = MultimediaPositionMap[storagePosition];

    const reason = {
        userAgent: navigator.userAgent,
        errorMessages: [
            ...(error?.message ? [error.message] : [String(error)]),
            ...additionalMessages,
        ].filter(Boolean),
    };

    // @TODO(corneliu-iancu): Temporary dual analytics implementation.
    // We are currently in the process of migrating from $superAnalytics to AnalyticsSDK.
    // Both libraries are being used in parallel to validate data consistency between them.
    // DO NOT modify or remove calls to $superAnalytics until the migration is fully validated and approved.
    // Once validation is complete, $superAnalytics will be deprecated and removed.
    AnalyticsSDK.getInstance().log('multimedia_request_result', {
        content_type: MultimediaContentTypeEnum.STREAM,
        match_id: getParamWithPrefix(Prefix.AXILIS, ParamTypes.MATCH, match?.id) ?? '',
        sport_id: getParamWithPrefix(Prefix.AXILIS, ParamTypes.SPORT, match?.sportId) ?? '',
        is_successful: isSuccessful === 'true',
        request_location: MultimediaRequestLocationEnum.NAVIGATION,
        position: paPosition,
        reason: JSON.stringify(reason),
    });

    Vue.prototype.$superAnalytics.sendEvent({
        eventType: AnalyticsEventTypes.multimediaRequestResult,
        multimediaRequestResult: {
            contentType: MultimediaContentType.STREAM,
            isSuccessful: isSuccessful,
            matchId: getParamWithPrefix(Prefix.AXILIS, ParamTypes.MATCH, match?.id),
            sportId: getParamWithPrefix(Prefix.AXILIS, ParamTypes.SPORT, match?.sportId),
            requestLocation: MultimediaRequestLocation.NAVIGATION,
            position,
            reason: { value: JSON.stringify(reason) },
        },
        AnalyticsEvent: AnalyticsProtoEvents.multimediaRequestResult,
    });
}

export const MultimediaPositionType = {
    'top-left': 1,
    'bottom-left': 2,
    'bottom-right': 3,
    'top-right': 4,
} as const;

// migration-v2 to replace the previous definition.
export const MultimediaPositionMap = {
    'top-left': MultimediaPosition.UPPER_LEFT,
    'bottom-left': MultimediaPosition.LOWER_LEFT,
    'bottom-right': MultimediaPosition.LOWER_RIGHT,
    'top-right': MultimediaPosition.UPPER_RIGHT,
} as const;
